import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin } from 'lucide-react';
import EnhancedPhoneButton from './EnhancedPhoneButton';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex flex-col items-center md:items-start mb-6">
              <img 
                src="https://i.imgur.com/H2WSFGE.jpg" 
                alt="Pinnacle Home Theater Logo" 
                className="h-20 w-auto rounded mb-3"
              />
              <span className="text-xl font-heading font-bold text-center md:text-left">
                Pinnacle Home Theater
              </span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed text-center md:text-left">
              Professional audio/video installation and maintenance services for homes and businesses in Middle Tennessee.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://facebook.com/pinnaclehometheater"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-secondary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
              <a
                href="https://instagram.com/pinnaclehometheater"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-secondary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
              <a
                href="https://linkedin.com/company/pinnaclehometheater"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-secondary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="text-center md:text-left">
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-4">
              <li>
                <Link to="/services" className="text-gray-400 hover:text-secondary transition-colors">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-400 hover:text-secondary transition-colors">
                  Gallery
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-400 hover:text-secondary transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-secondary transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/builders" className="text-gray-400 hover:text-secondary transition-colors">
                  For Builders
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="text-center md:text-left">
            <h3 className="text-lg font-semibold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start justify-center md:justify-start">
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                  <div className="ml-4">
                    <EnhancedPhoneButton
                      variant="outline"
                      size="sm"
                      showIcon={false}
                      trackingLabel="Footer Phone Button"
                      className="text-gray-400 hover:text-secondary border-none bg-transparent p-0 hover:bg-transparent"
                    />
                  </div>
                </div>
              </li>
              <li className="flex items-start justify-center md:justify-start">
                <Mail className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                <div className="ml-4 space-y-1">
                  <a
                    href="mailto:hometheaternashvilletn@gmail.com"
                    className="text-gray-400 hover:text-secondary transition-colors break-all block"
                  >
                    hometheaternashvilletn@gmail.com
                  </a>
                  <a
                    href="mailto:pinnaclehometheater@hotmail.com"
                    className="text-gray-400 hover:text-secondary transition-colors break-all block"
                  >
                    pinnaclehometheater@hotmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start justify-center md:justify-start">
                <MapPin className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <p className="text-gray-400">
                    Serving Nashville, Brentwood, Franklin,<br />
                    Gallatin, Mount Juliet, and<br />
                    surrounding areas in Middle TN
                  </p>
                </div>
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div className="text-center md:text-left">
            <h3 className="text-lg font-semibold mb-6">Business Hours</h3>
            <div className="flex items-start justify-center md:justify-start">
              <Clock className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
              <div className="ml-4">
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Weekdays</h4>
                  <p className="text-gray-400">8:00 AM - 6:00 PM</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Weekends</h4>
                  <p className="text-gray-400">Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-center md:text-left text-sm">
              © {currentYear} Pinnacle Home Theater. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-secondary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-secondary transition-colors">
                Terms of Service
              </Link>
              <a 
                href="https://www.pinnaclehometheater.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-secondary transition-colors"
              >
                Legacy Website
              </a>
              <Link to="/admin" className="text-gray-500 hover:text-secondary transition-colors opacity-50">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;